import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CornerUpLeft, Copy, Download, Forward, Pencil, Pin, PinOff, Plus, Star, StarOff, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import EmojiPicker from './EmojiPicker';
import './chat.css';

const QUICK_REACTIONS = ['❤️', '😂', '😮', '😢', '🙏', '👍'];

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
    } catch (err) {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        el.remove();
    }
    toast.success('Copied');
};

const downloadAttachment = async (attachment, base = '') => {
    const rawUrl = String(attachment?.url || '');
    if (!rawUrl) return;
    const src = rawUrl.startsWith('/') ? `${String(base).replace(/\/$/, '')}${rawUrl}` : rawUrl;
    let filename = attachment.name;
    if (!filename) {
        try { filename = decodeURIComponent(rawUrl.split('/').pop().split('?')[0]); }
        catch { filename = 'attachment'; }
    }

    try {
        const response = await fetch(src);
        if (!response.ok) throw new Error('Download failed');
        const objectUrl = URL.createObjectURL(await response.blob());
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename || 'attachment';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        toast.success('Download started');
    } catch {
        toast.error('Could not download this attachment');
    }
};

// The WhatsApp-style menu: a reaction bar on top and the message actions below.
const MessageMenu = ({ menu, onClose, onAction, onReply, onEdit, onForward, isOwn, canForward, attachmentBase }) => {
    const { item } = menu;
    const [more, setMore] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [pos, setPos] = useState({ left: menu.x, top: menu.y });
    const ref = useRef(null);

    const own = isOwn(item);
    const deleted = Boolean(item.deleted);
    const legacy = Boolean(item.legacy);
    const myReaction = item.reactions?.find(r => r.mine)?.emoji;

    // Keep the menu on screen.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const { width, height } = el.getBoundingClientRect();
        setPos({
            left: Math.max(8, Math.min(menu.x, window.innerWidth - width - 8)),
            top: Math.max(8, Math.min(menu.y, window.innerHeight - height - 8)),
        });
    }, [menu.x, menu.y, more, confirmDelete]);

    useEffect(() => {
        const onDown = e => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        document.addEventListener('keydown', onKey);
        window.addEventListener('resize', onClose);
        window.addEventListener('scroll', onClose, true);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
            document.removeEventListener('keydown', onKey);
            window.removeEventListener('resize', onClose);
            window.removeEventListener('scroll', onClose, true);
        };
    }, [onClose]);

    const run = (fn) => { fn(); onClose(); };
    const toggle = (action, label) => run(() => { onAction(item, action); toast.success(label); });
    const react = (emoji) => run(() => onAction(item, 'react', { emoji }));

    let body;
    if (confirmDelete) {
        body = (
            <div className="msg-menu__confirm">
                <p>Delete message?</p>
                <button type="button" onClick={() => run(() => onAction(item, 'delete', { scope: 'me' }))}>Delete for me</button>
                {own && !deleted && <button type="button" className="is-danger" onClick={() => run(() => onAction(item, 'delete', { scope: 'everyone' }))}>Delete for everyone</button>}
                <button type="button" className="is-plain" onClick={onClose}>Cancel</button>
            </div>
        );
    } else if (more) {
        body = <EmojiPicker onPick={react} />;
    } else {
        body = (
            <>
                {!deleted && !legacy && (
                    <div className="msg-menu__react">
                        {QUICK_REACTIONS.map(e => (
                            <button key={e} type="button" className={myReaction === e ? 'is-mine' : ''} onClick={() => react(e)} aria-label={`React ${e}`}>{e}</button>
                        ))}
                        <button type="button" className="msg-menu__more" onClick={() => setMore(true)} aria-label="More reactions"><Plus size={16} /></button>
                    </div>
                )}
                <div className="msg-menu__list">
                    {!deleted && !legacy && <button type="button" onClick={() => run(() => onReply(item))}><CornerUpLeft size={16} /> Reply</button>}
                    {!deleted && item.text && <button type="button" onClick={() => run(() => copyToClipboard(item.text))}><Copy size={16} /> Copy</button>}
                    {!deleted && item.attachment?.url && <button type="button" onClick={() => run(() => downloadAttachment(item.attachment, attachmentBase))}><Download size={16} /> Download</button>}
                    {!deleted && !legacy && canForward(item) && <button type="button" onClick={() => run(() => onForward(item))}><Forward size={16} /> Forward</button>}
                    {!deleted && !legacy && <button type="button" onClick={() => toggle('pin', item.pinned ? 'Unpinned' : 'Pinned')}>{item.pinned ? <><PinOff size={16} /> Unpin</> : <><Pin size={16} /> Pin</>}</button>}
                    {!deleted && !legacy && <button type="button" onClick={() => toggle('star', item.starred ? 'Unstarred' : 'Starred')}>{item.starred ? <><StarOff size={16} /> Unstar</> : <><Star size={16} /> Star</>}</button>}
                    {!deleted && !legacy && own && item.text && <button type="button" onClick={() => run(() => onEdit(item))}><Pencil size={16} /> Edit</button>}
                    {!legacy && <button type="button" className="is-danger" onClick={() => setConfirmDelete(true)}><Trash2 size={16} /> Delete</button>}
                </div>
            </>
        );
    }

    return createPortal(
        <div className="msg-menu" ref={ref} style={{ left: pos.left, top: pos.top }} role="menu">
            {body}
        </div>,
        document.body,
    );
};

/**
 * Wires the menu to a chat.
 *   onAction(item, action, payload)  -> do react / star / pin / delete against the server
 *   onReply(item) / onEdit(item) / onForward(item)
 *   isOwn(item)                      -> is this message written by the viewer
 *   canForward(item)                 -> show "Forward"
 *   attachmentBase                   -> server origin for relative attachment URLs
 * Returns { bind(item), openFrom(item, event), element } — spread bind(item) on the bubble wrapper, render `element` once.
 */
export default function useMessageMenu({ onAction, onReply, onEdit, onForward, isOwn, canForward = () => false, attachmentBase = '' }) {
    const [menu, setMenu] = useState(null);
    const timer = useRef(null);

    const close = useCallback(() => setMenu(null), []);
    const open = useCallback((item, point) => setMenu({ item, x: point.clientX, y: point.clientY }), []);

    const cancelPress = () => clearTimeout(timer.current);

    const bind = (item) => ({
        onContextMenu: (e) => { e.preventDefault(); open(item, e); },
        onTouchStart: (e) => {
            const touch = e.touches[0];
            cancelPress();
            timer.current = setTimeout(() => open(item, { clientX: touch.clientX, clientY: touch.clientY }), 450);
        },
        onTouchEnd: cancelPress,
        onTouchMove: cancelPress,
        onTouchCancel: cancelPress,
    });

    // The little chevron on a bubble.
    const openFrom = (item, event) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        open(item, { clientX: rect.left, clientY: rect.bottom + 4 });
    };

    useEffect(() => () => clearTimeout(timer.current), []);

    const element = menu ? (
        <MessageMenu
            menu={menu}
            onClose={close}
            onAction={onAction}
            onReply={onReply}
            onEdit={onEdit}
            onForward={onForward}
            isOwn={isOwn}
            canForward={canForward}
            attachmentBase={attachmentBase}
        />
    ) : null;

    return { bind, openFrom, element };
}
